import { isElementPartiallyInViewport, isInViewport } from '~/utils/dom';

export interface IIframelessWidgetInterface {
  /** Widget was loaded after it has been added to the dom */
  widgetLoaded: boolean;
  /** Widget was viewed at least once after it has been added to the dom */
  widgetWasViewed: boolean;
  /** Custom options for Intersection Observer */
  intersectionObserverOptions: IntersectionObserverInit;
  /** Load the widget */
  loadWidget(): void;
  /** Unique widget ID to create it with */
  // readonly id: string;
  /** Get boolean if the widget is currently in a user view */
  readonly isInView: boolean;
  /** Unique widget ID to create it with */
  readonly id: string;
  /** The host div of the widget */
  readonly hostDiv: HTMLDivElement;
  /** The app attachment div inside the host div */
  readonly attachmentDiv: HTMLDivElement;
  /** Add callback to queue to call once the widget is loaded after it has been added to the DOM. */
  onLoad(cb: (hostDiv: HTMLDivElement) => void): void;
}

/**
 * Abstract class that used as a blueprint for Widgets
 * @namespace Iframe
 * @abstract
 */
export abstract class IframelessWidget implements IIframelessWidgetInterface {
  public widgetLoaded: boolean;
  public widgetWasViewed: boolean;
  private onLoadCallbacks: Set<(hostDiv: HTMLDivElement) => void>;
  public readonly attachmentDiv: HTMLDivElement;

  /**
   * @param {string} id - Unique Widget ID to create it with
   * @param {object} params - Widget params
   * @param hostDiv
   * @param scriptUrl
   * @param cssUrl
   * @param lazyLoad
   * @param intersectionObserverOptions
   * @throws Will throw an error if the ID or url argument is falsy.
   */
  constructor(
    readonly hostDiv: HTMLDivElement,
    public readonly id: string,
    readonly scriptUrl: string,
    readonly params: { [key: string]: any } = {},
    public lazyLoad: boolean = true,
    public intersectionObserverOptions: IntersectionObserverInit = {}
  ) {
    /** Set a few guardian  */
    if (!this.hostDiv) {
      throw new Error('Widget host location is required to be present on the page');
    }

    if (!this.id) {
      throw new Error('Widget id is required');
    }

    if (!scriptUrl) {
      throw new Error('Widget url is required');
    }

    this.widgetLoaded = false;
    this.widgetWasViewed = false;
    this.onLoadCallbacks = new Set();

    this.onLoad((widgetHostDiv) =>
      widgetHostDiv.dispatchEvent(new MessageEvent('message', { data: { configuration: params } }))
    );

    const appAttachmentDiv = document.createElement('div');
    appAttachmentDiv.id = `${hostDiv.id}-${this.id}`;
    this.attachmentDiv = appAttachmentDiv;
    this.hostDiv.appendChild(appAttachmentDiv);

    if (!this.lazyLoad) {
      this.loadWidget();
    }
  }

  /**
   * Attach the iframe url and onload listener
   */
  public loadWidget() {
    const scriptElem = document.createElement('script');
    scriptElem.src = this.scriptUrl;
    scriptElem.async = true;
    scriptElem.addEventListener('load', () => this.widgetOnLoad());
    this.hostDiv.appendChild(scriptElem);
  }

  /**
   * Checks if element is in view currently
   * @returns boolean
   */
  get isInView(): boolean {
    if (isElementPartiallyInViewport(this.hostDiv) || isInViewport(this.hostDiv)) {
      this.widgetWasViewed = true;
      return true;
    }
    return false;
  }

  /**
   * Add callback to queue to call once widget is loaded after it has been added to the DOM.
   * @param {Function} cb - callback function which should be fired on iframe load
   * @returns {void}
   */
  public onLoad = (cb: (hostDiv: HTMLDivElement) => void): void => {
    if (typeof cb !== 'function') {
      return;
    }

    // ⬇ Call callback instantly if iFrame is already loaded
    if (this.widgetLoaded) {
      return cb(this.hostDiv);
    }
    // ⬇ Add callback to queue
    if (typeof cb === 'function') {
      this.onLoadCallbacks.add(cb);
    }
  };

  /**
   * Widget onload handler which will call onload callbacks once iFrame loaded
   * @returns {void}
   */
  protected widgetOnLoad = (): void => {
    this.widgetLoaded = true;
    if (this.onLoadCallbacks.size > 0) {
      this.onLoadCallbacks.forEach((callback) => callback(this.hostDiv));
    }
  };
}

export default IframelessWidget;
